import React, { useRef, useState, useEffect } from 'react'
import { projects } from '../data/projects'
import { ProjectCard } from '../components/ProjectCard'
import { ContactForm } from '../components/ContactForm'
import Modal from '../components/Modal'
import {
  Container,
  Section,
  Title,
  Subtitle,
  Grid,
} from '../styles/common.styles'
import styled from 'styled-components'

const ProjectsContainer = styled(Container)`
  min-height: 100vh;
  background-color: #0a0a0a;
  color: #ffffff;
  padding-top: 8rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    sans-serif;
  overflow-x: hidden;
  scroll-behavior: smooth;

  @media (max-width: 768px) {
    padding-top: 6rem;
  }
`

const ProjectsGrid = styled(Grid)`
  @media (min-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`

const ProjectCardWrapper = styled.div`
  scroll-margin-top: 120px; // Matches headerOffset
  transition: transform 0.3s ease;

  &:focus-within {
    transform: translateY(-4px);
  }
`

export function ProjectsPage() {
  const projectRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedProject, setSelectedProject] = useState<string | null>(null)
  const [formSuccess, setFormSuccess] = useState(false)

  const handleCardRef = (id: string, element: HTMLDivElement | null) => {
    projectRefs.current[id] = element
  }

  const handleViewSource = (projectId: string) => {
    setSelectedProject(projectId)
    setIsModalOpen(true)
    setFormSuccess(false)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setSelectedProject(null)
    setFormSuccess(false)
  }

  const handleContactSuccess = () => {
    setFormSuccess(true)
  }

  const getProjectName = (projectId: string) => {
    const project = projects.find((p) => p.id === projectId)
    return project?.title || 'Project'
  }

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '')
      setTimeout(() => {
        const el = document.getElementById(id)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 0)
    }
  }, [])

  return (
    <ProjectsContainer>
      <Section>
        <Title>My Projects</Title>
        <Subtitle>Here are some of my recent works</Subtitle>
        <ProjectsGrid>
          {projects.map((project) => (
            <ProjectCardWrapper
              key={project.id}
              id={project.id}
              ref={(el) => handleCardRef(project.id, el)}>
              <ProjectCard
                project={project}
                variant='page'
                onViewDemo={() => window.open(project.link, '_blank')}
                onViewSource={() => handleViewSource(project.id)}
              />
            </ProjectCardWrapper>
          ))}
        </ProjectsGrid>
      </Section>

      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title={`Request Access to ${
          selectedProject ? getProjectName(selectedProject) : 'Project'
        } Source Code`}>
        {formSuccess ? (
          <div
            style={{
              textAlign: 'center',
              padding: '2rem 0',
              fontSize: '1.1rem',
              color: '#1a7f5a',
              fontWeight: 600,
            }}>
            Thank you for your message! I will get back to you soon.
          </div>
        ) : (
          <ContactForm
            onSuccess={handleContactSuccess}
            defaultMessage={`I would like to request access to the source code for ${
              selectedProject ? getProjectName(selectedProject) : 'this project'
            }.`}
            variant='light'
          />
        )}
      </Modal>
    </ProjectsContainer>
  )
}
